import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { ChipGroup } from "./fields";
import { X } from "lucide-react";
import { toast } from "sonner";

const COMPLETION = [0, 25, 50, 75, 100];

export default function BlockEntrySheet({ student, block, date, onClose, onSaved, hasNext = false }) {
  const [tax, setTax] = useState(null);
  const [accOptions, setAccOptions] = useState([]);
  const existing = block.entry;
  const [form, setForm] = useState({
    attendance: existing?.attendance || "Present",
    support_provided: existing?.support_provided || [],
    prompting_level: existing?.prompting_level || "Verbal",
    engagement: existing?.engagement || "Mostly Engaged",
    work_completion: existing?.work_completion ?? 100,
    behavior_status: existing?.behavior_status || "Regulated",
    independence: existing?.independence || "Some Support",
    accommodations_used: existing?.accommodations_used || [],
    note: existing?.note || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/meta/taxonomies").then((r) => setTax(r.data));
    api.get(`/students/${student.id}`).then((r) => setAccOptions(r.data.profile.accommodations || []));
  }, [student.id]);

  const submit = async (goNext = false) => {
    if (!form.note.trim()) { toast.error("A brief note is required — one observable sentence is enough."); return; }
    setSaving(true);
    try {
      const body = { ...form, student_id: student.id, date, block_id: block.block_id, block_name: block.name, block_time: `${block.start}–${block.end}`, location: block.location };
      if (existing) await api.put(`/block-entries/${existing.id}`, body);
      else await api.post("/block-entries", body);
      toast.success(`${block.name} documented`);
      onSaved(goNext);
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (!tax) return null;
  const absent = form.attendance === "Absent";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="block-entry-sheet" className="sf-sheet bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-semibold text-slate-900">{block.name}</h2>
            <p className="text-xs text-slate-400">{student.first_name} {student.last_name} · {block.start}–{block.end} · {block.location}</p>
          </div>
          <button data-testid="close-block-sheet-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <ChipGroup label="Attendance" options={["Present", "Absent"]} value={form.attendance} onChange={set("attendance")} testPrefix="attendance" />
          {!absent && (
            <>
              <ChipGroup label="Support provided" options={tax.support_options} value={form.support_provided} onChange={set("support_provided")} multi testPrefix="support" />
              <ChipGroup label="Prompting level" options={tax.prompt_levels} value={form.prompting_level} onChange={set("prompting_level")} testPrefix="prompting" />
              <ChipGroup label="Engagement" options={tax.engagement_options} value={form.engagement} onChange={set("engagement")} testPrefix="engagement" />
              <ChipGroup label="Work completion" options={COMPLETION.map(String)} value={String(form.work_completion)} onChange={(v) => set("work_completion")(Number(v))} testPrefix="completion" />
              <ChipGroup label="Behavior / regulation" options={tax.behavior_status_options} value={form.behavior_status} onChange={set("behavior_status")} testPrefix="behavior-status" />
              <ChipGroup label="Independence" options={tax.independence_options} value={form.independence} onChange={set("independence")} testPrefix="independence" />
              {accOptions.length > 0 && <ChipGroup label="Accommodations used" options={accOptions} value={form.accommodations_used} onChange={set("accommodations_used")} multi testPrefix="accommodation" />}
            </>
          )}
          <div>
            <p className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500 mb-2">Brief note — required, observable language only</p>
            <textarea data-testid="block-note-input" value={form.note} onChange={(e) => set("note")(e.target.value)} rows={2} required
              placeholder="e.g., Returned to task after a three-minute movement break."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex gap-2.5">
          <button data-testid="submit-block-entry-btn" onClick={() => submit(false)} disabled={saving}
            className="flex-1 bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
            {saving ? "Saving…" : existing ? "Update Documentation" : "Save Documentation"}
          </button>
          {hasNext && !existing && (
            <button data-testid="save-next-block-btn" onClick={() => submit(true)} disabled={saving}
              className="flex-1 bg-[#0F1F38] hover:bg-[#1a2f4f] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
              Save & Next Block
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
